import axios from "axios";
import Swal from "sweetalert2";
import { BASE_URL, downloadURL, UPLOAD_ROOT_PATH } from "./Constants";
import { isObjectStorageHttpUrl } from "./objectStorage";

export const download = async (fileUrl, fileName) => {
  if (fileUrl) {
    // replace port if fileurl has a :<port number in with ""
    fileUrl = fileUrl.replace(/:\d+/, "");

    if (fileUrl.includes(downloadURL)) {
      // Remove Port Numbers (":3333"), corona url e.g.: https://corona.myaie.ac/myaie.ac/Library/feedResources/post-123456-789-01-2345678912.pdf
      fileUrl = fileUrl.replace(/:\d{4,5}/g, "");
    }
  }

  Swal.close();
  Swal.fire({
   icon: "info",
   title: "Downloading...",
   timer: 2000,
   onOpen: function () {
     Swal.showLoading();
   },
   showConfirmButton: false
 }).then((result) => {
 });
  let flag = 0;
  await axios({
    url: fileUrl, 
    method: "GET",
    responseType: "blob", // important
  }).then((response) => {
    console.log(response);
    if (response.status==200) {
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", fileName); 
      document.body.appendChild(link);
      link.click();
      flag = 1;
      Swal.close();
      Swal.fire({
       icon: "success",
       title: "Downloading Finished",
       showConfirmButton: true
     }).then((result) => {
     });
    } else {
      Swal.fire({
        icon: "error",
        title: "Error while downloading file",
      }).then((result) => {});
    }

  }).catch((error) => {   
    Swal.fire({
      icon: "error",
      title: "Error while downloading file",
    }).then((result) => {});
  });
  if (flag) return flag;
};

/**
 * Removes public/ and `${UPLOAD_ROOT_PATH}/` path to prepend a base url.
 * @param {string} filePath Path to transform
 * @returns clean relative file path
 */
export const createCleanFilePath = (filePath) => {
  if (!filePath) {
    return filePath;
  }

  return filePath
    .replaceAll(`${UPLOAD_ROOT_PATH}/`, '')
    .replace('public/', '');
};

/**
 * Percent-encodes a path once, so a stored path that is already encoded isn't double-encoded.
 * @param {string} filePath Raw or already-encoded path.
 */
function encodeFilePath(filePath) {
  return encodeURI(filePath).replace(/%25([0-9A-Fa-f]{2})/g, "%$1");
}

/**
 * Builds an encoded, corona-wrapped download URL by removing UPLOAD_ROOT_PATH (e.g. '/home/myaie/public_html').
 * S3/R2 and other external URLs are returned unchanged. Backslash paths skip corona,
 * which rewrites "%5C" to "/" so those folders 404.
 *
 * Edge case: paths starting with "QuestionDocument/" are stored without the
 * "Library/" prefix, so "Library/" is prepended to them.
 *
 * @param {string} filePath * filePath Examples:
 * filePath = "/home/myaie/public_html/Library/feedResources/file.pdf"
 * filePath = "/home/myaie/api/public/Library/feedResources/file.pdf"
 * filePath = "https://corona.myaie.ac/myaie.ac/Library/file.pdf"
 * filePath = "Library/feedResources/file.pdf"
 * filePath = "QuestionDocument/file.pdf" (becomes "Library/QuestionDocument/file.pdf")
 * filePath = "https://example.com/file.pdf" (unchanged)
 * @returns clean absolute downloadable URL
 */
export const buildAbsoluteDownloadUrl = (filePath) => {
  if (!filePath || isObjectStorageHttpUrl(filePath)) {
    return filePath;
  }

  const cleanDownloadUrl = downloadURL.replace(/\/$/, "");
  const baseHost = BASE_URL.replace(/^https?:\/\//, "").replace(/\/$/, "");
  const toDownloadUrl = (relative) => (
    relative.includes("\\")
      ? `https://${baseHost}/${encodeFilePath(relative)}`
      : `${cleanDownloadUrl}/${baseHost}/${encodeFilePath(relative)}`
  );

  let cleaned = filePath.replace(/^https?:\/\//, "");

  cleaned = cleaned.replace(new RegExp(`^(www\\.)?${baseHost}/`), "");
  cleaned = cleaned.replace(/^corona\.myaie\.ac\//, "");

  let relativePath = cleaned.replace(/.*\/api\/public\//, "");

  if (relativePath === cleaned) {
    relativePath = cleaned.replace(/.*\/public_html\//, "");
  }

  if (relativePath === cleaned) {
    relativePath = createCleanFilePath(cleaned);
  }

  const isKnownPath =
    relativePath !== cleaned ||
    cleaned.includes("/public_html/") ||
    cleaned.includes("/api/public/");

  // Unknown structure
  if (!isKnownPath) {
    if (isValidUrl(filePath)) return filePath;

    let relative = cleaned.replace(/^\/+/, "");
    //QuestionDocument files live under Library/ but are saved without that prefix "Library".
    if (relative.startsWith("QuestionDocument/")) {
      relative = `Library/${relative}`;
    }
    return toDownloadUrl(relative);
  }

  let finalRelative = relativePath.replace(/^\/+/, "");

  if (finalRelative.startsWith("public/")) {
    // Legacy files can still be saved with "public/", simply remove it, because the files have been moved to baseUrl:
    finalRelative = finalRelative.replace(/^public\//, "");
  }
  // QuestionDocument files live under Library / but are saved without that prefix "Library".
  if (finalRelative.startsWith("QuestionDocument/")) {
    finalRelative = `Library/${finalRelative}`;
  }

  return toDownloadUrl(finalRelative);
};

const isValidUrl = (value) => {
  try {
    new URL(value);
    return true;
  } catch {
    return false;
  }
};
